import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useOTPVerification from './useOTPVerification';
import { useTranslation } from 'react-i18next';
import LoadingButton from '../../../utility/button/LoadingButton';

const OTPVerification = ({ type = 'email' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');

  const triggerResend = () => {
    if (!isDisabled) {
      setOtp(['', '', '', '', '', '']);
      handleResend(type);
      setIsDisabled(true);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    triggerResend();
  };

  const {
    heading,
    message,
    loading,
    handleSubmit,
    handleResend,
  } = useOTPVerification({ type, triggerResend });

  const [isDisabled, setIsDisabled] = useState(false);
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    let interval;

    if (isDisabled) {
      setTimer(30); // start countdown
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isDisabled]);

  const handleInputChange = (e, index) => {
    const value = e.target.value;
    if (!/^\d?$/.test(value)) return;

    const updatedOtp = [...otp];
    updatedOtp[index] = value;
    setOtp(updatedOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (!value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text').trim();

    // Only digits allowed, but paste as is
    if (!/^\d+$/.test(pastedData)) {
      return; // ignore if non-digit characters present
    }

    const pastedDigits = pastedData.split('');
    const updatedOtp = [...otp];

    // Fill the otp array starting from current index
    for (let i = 0; i < pastedDigits.length && (index + i) < 6; i++) {
      updatedOtp[index + i] = pastedDigits[i];
    }

    setOtp(updatedOtp);

    // Focus the input after the last pasted digit, or last input
    const focusIndex = Math.min(index + pastedDigits.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const fullOtp = otp.join('');
    if (fullOtp.length !== 6 || otp.some(d => d === '')) {
      setError(t('otpVerification.errorIncomplete'));
      return;
    }

    setError('');
    handleSubmit(fullOtp); // Send to hook
  };

  return (
    <div className="auth-card mx-auto text-center">
      <div className="d-flex justify-content-center mb-4">
        <div
          style={{
            background: '#f4f5fa',
            borderRadius: 10,
            width: 58,
            height: 58,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="38"
            height="38"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="38" height="38" rx="10" fill="#eceffe" />
            <path
              d="M28 13v12a1 1 0 01-1 1H11a1 1 0 01-1-1V13"
              stroke="#363b70"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M28 13l-9 7-9-7"
              stroke="#363b70"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
      <h3 className="fw-bold mb-1">{heading}</h3>
      <div className='forgot-sub-heading' style={{ fontSize: '1rem', marginBottom: 27 }}>
        {message}
      </div>

      <form
        onSubmit={handleFormSubmit}
        className="d-flex justify-content-center gap-2 mb-3"
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input
            key={i}
            ref={(el) => (inputRefs.current[i] = el)}
            type="text"
            maxLength={1}
            className="otp-input me-2 text-center"
            style={{ width: 48, height: 48, fontSize: 24 }}
            value={otp[i]}
            disabled={loading}
            onChange={(e) => handleInputChange(e, i)}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !e.target.value && i > 0) {
                inputRefs.current[i - 1]?.focus();
              }
            }}
            onPaste={(e) => handlePaste(e, i)}
          />
        ))}
        <button className="visually-hidden" type="submit" />
      </form>

      {error && <div className="text-danger mb-3">{error}</div>}

      <LoadingButton
        type="submit"
        disabled={loading}
        isLoading={loading}
        className=""
        style={{ width: '80%' }}
        onClick={handleFormSubmit}
      >
        {t('otpVerification.verify')}
      </LoadingButton>

      <div className="mt-2 mb-3" style={{ fontSize: 15 }}>
        {t('otpVerification.notReceived')}{' '}
        <a
          className={`btn-link ${!isDisabled ? 'signin-section-link' : 'disable-link'} `}
          href="#"
          onClick={handleClick}
          style={{
            pointerEvents: isDisabled ? 'none' : 'auto',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          <b>
            {isDisabled
              ? `${t('otpVerification.resend')} (${timer}s)`
              : t('otpVerification.resend')}
          </b>
        </a>
      </div>
    </div>
  );
};

export default OTPVerification;
